import { FormEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  FormGroup,
  Label,
  Divider,
  Input,
  Search,
  SearchIcon,
  AddressItem,
} from "@mwi/ui";

import { useGetPostcodeAddressesQuery, useRegisterMutation } from "api";
import { RootState } from "store";
import { setVerifyMethods, updateFormState } from "slices/registration";
import { login } from "slices/auth";
import { gtmEvent } from "helpers/gtm";
import { isFetchBaseQueryError } from "helpers/rtk";
import useErrorHandler from "hooks/useErrorHandler";

type Inputs = {
  previous_practice: boolean;
  previous_practice_name: string;
  previous_practice_address_postcode: string;
  previous_practice_address_line_1: string;
  previous_practice_address_line_2: string;
  previous_practice_address_town: string;
  previous_practice_address_county: string;
  previous_practice_phone: string;
};

const validatePhone = (phone: string) => /^\+?\d{9,15}$/.test(phone);

const RegistrationPreviousPractice = () => {
  const dispatch = useDispatch();
  const errorHandler = useErrorHandler();

  const [hasPreviousPractice, setHasPreviousPractice] = useState<boolean>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchError, setSearchError] = useState<string>("");

  const { formState } = useSelector((state: RootState) => ({
    formState: state.registration.formState,
  }));

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      previous_practice: formState.previous_practice,
      previous_practice_name: formState.previous_practice_name,
      previous_practice_address_postcode:
        formState.previous_practice_address_postcode,
      previous_practice_address_line_1:
        formState.previous_practice_address_line_1,
      previous_practice_address_line_2:
        formState.previous_practice_address_line_2,
      previous_practice_address_town: formState.previous_practice_address_town,
      previous_practice_address_county:
        formState.previous_practice_address_county,
      previous_practice_phone: formState.previous_practice_phone,
    },
  });

  const [submitRegister, { data: registerResult, isLoading, error }] =
    useRegisterMutation();

  const { data: postcodeAddresses, error: postcodeSearchError } =
    useGetPostcodeAddressesQuery({ searchTerm }, { skip: !searchTerm });

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
    }
  }, [error]);

  useEffect(() => {
    const searchError = postcodeSearchError as any;
    setSearchError("");
    if (searchError) {
      // We manually set an error message for >= 500 errors in JSX below for address step
      if (
        isFetchBaseQueryError(postcodeSearchError) &&
        typeof searchError.status === "number" &&
        searchError.status < 500 &&
        searchError.data?.errors?.[0]?.message
      ) {
        setSearchError(searchError.data.errors[0].message);
      }
    } else if (postcodeAddresses === null) {
      setSearchError(
        "Sorry, we are unable to find your postcode. Please enter your address using the form below.",
      );
    }
  }, [postcodeSearchError, postcodeAddresses]);

  useEffect(() => {
    if (registerResult) {
      const values = getValues();
      dispatch(
        updateFormState({
          previous_practice: hasPreviousPractice ? true : false,
          previous_practice_name: values.previous_practice_name,
          previous_practice_address_postcode:
            values.previous_practice_address_postcode,
          previous_practice_address_line_1:
            values.previous_practice_address_line_1,
          previous_practice_address_line_2:
            values.previous_practice_address_line_2,
          previous_practice_address_town: values.previous_practice_address_town,
          previous_practice_address_county:
            values.previous_practice_address_county,
          previous_practice_phone: values.previous_practice_phone,
        }),
      );

      dispatch(login({ user: registerResult }));
      dispatch(
        setVerifyMethods([
          { method: "sms", display: formState.phone! },
          { method: "email", display: formState.email },
        ]),
      );
    }
  }, [registerResult]);

  const executeRegistration = (data?: Inputs) => {
    const previous_practice_details =
      data && hasPreviousPractice
        ? `${data.previous_practice_name && `${data.previous_practice_name},`} ${data.previous_practice_address_line_1 && `${data.previous_practice_address_line_1},`} ${data.previous_practice_address_line_2 && `${data.previous_practice_address_line_2},`} ${data.previous_practice_address_town && `${data.previous_practice_address_town},`} ${data.previous_practice_address_county && `${data.previous_practice_address_county},`} ${data.previous_practice_address_postcode && `${data.previous_practice_address_postcode}.`} ${data.previous_practice_phone && `Phone: ${data.previous_practice_phone}`}`
        : null;
    submitRegister({
      email: formState.email,

      title_uuid: formState.title_uuid,
      first_name: formState.first_name!,
      last_name: formState.last_name!,
      phone: formState.phone!,
      terms: formState.terms!,

      name_no: formState.name_no!,
      line_1: formState.line_1!,
      line_2: formState.line_2!,
      city: formState.city!,
      county: formState.county!,
      postcode: formState.postcode!,

      vet_site: formState.vet_site!,

      ...(previous_practice_details && { previous_practice_details }), // conditionally add key if user has previous practice
    });
  };

  const handleHasPreviousPracticeClick = (hasPrevious: boolean) => {
    if (!hasPrevious) {
      executeRegistration();
    } else {
      setHasPreviousPractice(hasPrevious);
    }
  };

  const submitForm = (data: Inputs) => {
    clearErrors();
    setSearchError("");
    let error = false;
    if (!data.previous_practice_name) {
      error = true;
      setError("previous_practice_name", {
        message: "The name of your previous practice is required",
      });
    }

    if (
      data.previous_practice_phone &&
      !validatePhone(data.previous_practice_phone)
    ) {
      error = true;
      setError("previous_practice_phone", {
        message: "Please enter a valid phone number",
      });
    }

    if (!error) {
      executeRegistration(data);
    }
  };

  useEffect(
    () => gtmEvent({ name: "onRegFlowPrevVetLocationStepPageView" }),
    [],
  );

  return (
    <div className="py-6">
      <h1 className="text-4xl font-bold tablet:text-6xl">
        Details of previously registered practice
      </h1>

      <div className="mt-8 tablet:mt-12">
        <Label>Were you previously registered with another practice?</Label>
        <div className="grid grid-cols-1 tablet:gap-6 tablet:grid-cols-2">
          <Button
            data-test-id="PreviousPractice-Yes_Button"
            className="mt-4 inline"
            variant={
              hasPreviousPractice == null || hasPreviousPractice === true
                ? "primary"
                : "outlined"
            }
            onClick={() => handleHasPreviousPracticeClick(true)}
          >
            Yes
          </Button>
          <Button
            data-test-id="PreviousPractice-No_Button"
            className="mt-4 inline"
            variant={
              hasPreviousPractice == null || hasPreviousPractice === false
                ? "primary"
                : "outlined"
            }
            onClick={() => handleHasPreviousPracticeClick(false)}
          >
            No
          </Button>
        </div>
      </div>

      {hasPreviousPractice && (
        <form
          className="flex flex-col flex-grow mt-8 tablet:mt-12"
          onSubmit={handleSubmit(submitForm)}
        >
          <p className="mb-6 tablet:mb-8 tablet:text-lg">
            Please provide details about your previous practice
          </p>

          <FormGroup error={errors.previous_practice_name?.message}>
            <Label>Name of previous practice</Label>
            <Input
              data-test-id="PreviousPractice-Name_Input"
              {...register("previous_practice_name")}
            />
          </FormGroup>

          <FormGroup error={errors.previous_practice_phone?.message}>
            <Label>Phone Number</Label>
            <Input
              data-test-id="PreviousPractice-Phone_Input"
              type="tel"
              {...register("previous_practice_phone")}
            />
          </FormGroup>

          <FormGroup error={searchError}>
            <Label>Enter Postcode</Label>
            <Search
              data-test-id="PreviousPractice-Search_Input"
              prefixIcon={<SearchIcon />}
              placeholder="Start typing your postcode..."
              onChange={(value) => setSearchTerm(value)}
              setSelected={(value) => {
                clearErrors();
                setSearchError("");
                setValue("previous_practice_address_line_1", value?.line1);
                setValue("previous_practice_address_line_2", value?.line2);
                setValue("previous_practice_address_town", value?.city);
                setValue("previous_practice_address_county", value?.county);
                setValue("previous_practice_address_postcode", value?.postcode);
              }}
            >
              {postcodeAddresses &&
                postcodeAddresses.map((address, idx) => (
                  <AddressItem key={idx} value={address} />
                ))}
            </Search>
          </FormGroup>

          <Divider className="mt-2 mb-8" />

          <FormGroup error={errors?.previous_practice_address_line_1?.message}>
            <Label>Address Line 1</Label>
            <Input
              data-test-id="PreviousPractice-Address1_Input"
              {...register("previous_practice_address_line_1")}
            />
          </FormGroup>

          <FormGroup error={errors?.previous_practice_address_line_2?.message}>
            <Label>Address Line 2</Label>
            <Input
              data-test-id="PreviousPractice-Address2_Input"
              {...register("previous_practice_address_line_2")}
            />
          </FormGroup>

          <FormGroup error={errors?.previous_practice_address_town?.message}>
            <Label>Town / City</Label>
            <Input
              data-test-id="PreviousPractice-Town_Input"
              {...register("previous_practice_address_town")}
            />
          </FormGroup>

          <div className="grid grid-cols-1 tablet:gap-6 tablet:grid-cols-2">
            <FormGroup
              error={errors?.previous_practice_address_county?.message}
            >
              <Label>County</Label>
              <Input
                data-test-id="PreviousPractice-County_Input"
                {...register("previous_practice_address_county")}
              />
            </FormGroup>

            <FormGroup
              error={errors?.previous_practice_address_postcode?.message}
            >
              <Label>Postcode</Label>
              <Input
                data-test-id="PreviousPractice-Postcode_Input"
                {...register("previous_practice_address_postcode")}
              />
            </FormGroup>
          </div>

          <div className="mt-auto tablet:mt-12 tablet:max-w-xs">
            <Button
              data-test-id="PreviousPractice-Submit_Button"
              type="submit"
              block
              loading={isLoading}
            >
              Submit
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrationPreviousPractice;
